import React, { useEffect, useRef } from "react";

/**
 * Lightweight animated connected-dots particle field, inspired by the Koyta
 * portfolio hero. Pure canvas — no library — so it stays fast.
 *
 * Props:
 *  - density: number of particles per ~100k px² (default 0.045)
 *  - linkDistance: max distance to draw a connecting line
 *  - color: stroke color (CSS hex/rgb)
 */
const ParticleField = ({
  density = 0.045,
  linkDistance = 130,
  color = "#FFD968",
  speed = 0.35,
  className = "",
}) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(0);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0, dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const seed = () => {
      const target = Math.min(110, Math.max(40, Math.round(w * h * density / 100)));
      const arr = [];
      for (let i = 0; i < target; i++) {
        arr.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * speed,
          vy: (Math.random() - 0.5) * speed,
          r: Math.random() * 1.4 + 0.4,
        });
      }
      particlesRef.current = arr;
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (x >= 0 && x <= w && y >= 0 && y <= h) {
        mouseRef.current.x = x;
        mouseRef.current.y = y;
        mouseRef.current.active = true;
      } else {
        mouseRef.current.active = false;
      }
    };
    const onLeave = () => { mouseRef.current.active = false; };

    const tick = () => {
      ctx.clearRect(0, 0, w, h);

      const ps = particlesRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mouseRadius = 160;

      // Move + draw dots
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        // Soft mouse repulsion
        if (mouseRef.current.active) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < mouseRadius * mouseRadius) {
            const d = Math.sqrt(d2) || 0.01;
            const force = (1 - d / mouseRadius) * 0.6;
            p.x += (dx / d) * force;
            p.y += (dy / d) * force;
          }
        }

        ctx.beginPath();
        ctx.fillStyle = "rgba(255,217,104,0.85)";
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Connect close particles
      const max = linkDistance;
      const maxSq = max * max;
      ctx.lineWidth = 1;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        for (let j = i + 1; j < ps.length; j++) {
          const q = ps[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dSq = dx * dx + dy * dy;
          if (dSq < maxSq) {
            const a = 1 - dSq / maxSq;
            ctx.strokeStyle = `rgba(255,217,104,${a * 0.35})`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
        // Mouse link line
        if (mouseRef.current.active) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const dSq = dx * dx + dy * dy;
          if (dSq < maxSq) {
            const a = 1 - dSq / maxSq;
            ctx.strokeStyle = `rgba(250,248,245,${a * 0.4})`;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mx, my);
            ctx.stroke();
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    tick();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, [density, linkDistance, color, speed]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-particles"
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      aria-hidden="true"
    />
  );
};

export default ParticleField;
